/**
 * AI en spraak, via de eigen API.
 *
 * Dit bestand vervangt vier modules van samen ruim 800 regels:
 * `azure-ai-client.ts`, `translate-client.ts`, `icpc-suggest-client.ts` en
 * `azure-tts-client.ts`. Die riepen Azure AI Foundry rechtstreeks aan uit de
 * browser, met de sleutel die via `validate-access-token` was meegekomen.
 *
 * Wat er verdwijnt is niet alleen de sleutel. Die modules bevatten elk hun eigen
 * kandidatenlijst: vier modelnaam-varianten maal acht URL-vormen maal twee
 * bodyvormen maal twee auth-headers, plus list-calls om te ontdekken welke
 * deployment bestond. In het slechtste geval ruim honderd HTTP-requests zonder
 * timeout, binnen één gebruikersactie, met de gebruiker wachtend op de
 * platform-timeout. Dat probeer-en-zie-wat-werkt staat nu op de server, begrensd
 * tot vier kandidaten met een deadline, en de combinatie die werkt wordt onthouden.
 *
 * De prompts zijn hier ook weg. De browser stuurde `doctor_phrase`,
 * `provider_role`, `report_verbosity` en `personal_profile` mee naar de AI; de
 * server leest ze nu uit het profiel van de gebruiker die de sessie heeft. Een
 * client kan dus niet meer bepalen met welke instructie het model werkt.
 */

import { api } from './api';
import type {
  IcpcAntwoord,
  IcpcSuggestie,
  IcpcZoekAntwoord,
  SoepAntwoord,
  SpeechToken,
  VertaalAntwoord,
} from './api-types';

// --- Transcript naar SOEP --------------------------------------------------

/**
 * Zet een transcript om in een of meer SOEP-verslagen.
 *
 * `multipleProblems: false` dwingt precies één verslag af, ook bij meerdere
 * klachten -- dat is de `force_single_soep`-voorkeur van de gebruiker.
 */
export async function transcriptNaarSoep(
  transcript: string,
  multipleProblems: boolean,
): Promise<SoepAntwoord> {
  return api.post<SoepAntwoord>('/ai/soep', { transcript, multipleProblems });
}

// --- ICPC ------------------------------------------------------------------

/**
 * Stelt ICPC-codes voor bij een evaluatietekst.
 *
 * De server valideert wat het model teruggeeft tegen de codelijst in de database,
 * dus wat hier uitkomt bestaat gegarandeerd. Dat deed de oude client ook, maar in
 * twee stappen: eerst Azure aanroepen uit de browser, dan een aparte query om de
 * codes te controleren.
 */
export async function stelIcpcVoor(
  evaluationText: string,
): Promise<{ suggesties: IcpcSuggestie[]; kosten: number }> {
  if (!evaluationText || evaluationText.trim().length < 3) {
    return { suggesties: [], kosten: 0 };
  }

  const antwoord = await api.post<IcpcAntwoord>('/ai/icpc', { evaluationText });
  return { suggesties: antwoord.suggestions, kosten: antwoord.cost ?? 0 };
}

/**
 * Valideert codes die de gebruiker zelf heeft ingevoerd, zonder AI-aanroep.
 *
 * Bespaart een call, en belangrijker: er gaat dan geen consulttekst naar Azure.
 */
export async function valideerIcpcCodes(codes: string[]): Promise<IcpcSuggestie[]> {
  if (codes.length === 0) return [];

  const antwoord = await api.post<IcpcAntwoord>('/ai/icpc', { validateCodes: codes });
  return antwoord.suggestions;
}

/** Zoeken in de ICPC-codelijst. Was een directe tabelquery uit de browser. */
export async function zoekIcpc(
  q: string,
  limit = 15,
  signal?: AbortSignal,
): Promise<IcpcZoekAntwoord['results']> {
  if (!q.trim()) return [];

  const antwoord = await api.get<IcpcZoekAntwoord>('/icpc/search', {
    query: { q, limit },
    ...(signal ? { signal } : {}),
  });
  return antwoord.results;
}

// --- Vertalen -------------------------------------------------------------

export async function vertaal(
  text: string,
  targetLanguage = 'Nederlands',
  source: 'soep' | 'patient_mobile' | 'live_transcript' = 'soep',
): Promise<{ vertaling: string; kosten: number }> {
  if (!text.trim()) return { vertaling: '', kosten: 0 };

  const antwoord = await api.post<VertaalAntwoord>('/ai/translate', {
    text,
    targetLanguage,
    source,
  });
  return { vertaling: antwoord.translated, kosten: antwoord.cost };
}

// --- Spraak: het token voor de opname ------------------------------------

interface GecachtToken {
  token: string;
  region: string;
  verlooptOp: number;
}

let tokenCache: GecachtToken | null = null;

/**
 * Een kortlevend token voor de Speech SDK in de browser.
 *
 * De opname zelf moet in de browser blijven: de microfoon staat daar, en continue
 * herkenning over een websocket is niet iets om via de eigen server te leiden. Wat
 * er wel verandert is dat het endpoint een sessie vereist -- in de bron was
 * `azure-speech-token` volledig onbeschermd, dus ieder verzoek van internet liep op
 * kosten van iSOFT.
 *
 * De marge is een minuut ruimer dan de server aangeeft. Een token dat halverwege
 * een consult verloopt breekt de herkenning af, en dat is een duurdere fout dan een
 * keer te vroeg vernieuwen.
 */
export async function haalSpeechToken(): Promise<{ token: string; region: string }> {
  if (tokenCache && Date.now() < tokenCache.verlooptOp) {
    return { token: tokenCache.token, region: tokenCache.region };
  }

  const antwoord = await api.get<SpeechToken>('/speech/token');

  tokenCache = {
    token: antwoord.token,
    region: antwoord.region,
    verlooptOp: Date.now() + Math.max(0, antwoord.expiresInSeconds - 60) * 1000,
  };

  return { token: antwoord.token, region: antwoord.region };
}

/** Na een sessiewissel: het oude token hoort bij de vorige praktijk. */
export function wisSpeechTokenCache(): void {
  tokenCache = null;
}

// --- Spraak: tekst naar audio -------------------------------------------

/**
 * Synthetiseert tekst naar mp3, op de server.
 *
 * De vorige versie deed dit met de Speech SDK in de browser. Dat werkte, maar het
 * had twee gevolgen die niet zichtbaar waren: gesynthetiseerde spraak werd nooit
 * doorbelast -- opnames wel, dus dat was een gat in de facturering -- en `voice` en
 * `speed` gingen ongefilterd in de SSML die naar Azure ging. De server valideert nu
 * de stemnaam tegen een patroon en logt de duur.
 */
export async function tekstNaarSpraak(
  text: string,
  voice = 'nl-NL-FennaNeural',
  speed?: string,
): Promise<{ audio: Blob; kosten: number }> {
  const { blob, headers } = await api.blobMetHeaders('/speech/tts', {
    body: { text, voice, ...(speed ? { speed } : {}) },
  });

  return { audio: blob, kosten: Number(headers.get('X-Kosten') ?? '0') };
}
